<template>
    <div>
        <div v-if="programHasConfigurableAttachments">
            <pc-input
                v-model="fileTypes"
                v-bind:label="allowedAttachmentTypesLabel"
                v-bind:disabled="disableFileExtensions" />
            <div
                v-if="attachmentBlocklistedTypes.length > 0"
                class="alert alert-danger mt-3 mb-0">
                {{ blocklistedAttachmentTypesText }}
            </div>
            <div
                v-if="fileTypes.length <= 0"
                class="alert alert-danger mt-3 mb-0">
                {{ emptyAttachmentTypesText }}
            </div>
            <div
                v-if="attachmentCommunicationBlocklistedTypes.length > 0"
                class="alert mt-3 mb-0"
                v-bind:class="attachmentCommunicationAlertClass">
                {{ communicationUnsupportedAttachmentTypesText }}
            </div>
            <pc-alert
                class="mt-3 mb-0"
                v-bind:shown="canMerge && attachmentDisallowedMergeFileTypeExtensions.length > 0"
                v-bind:severity="warning">
                {{ mergeTypeUnsupportedAttachmentTypesText }}
            </pc-alert>
            <pc-alert
                class="mt-3 mb-0"
                v-bind:shown="hasReferenceDocuments"
                v-bind:severity="warning">
                {{ referenceDocumentExtensionsWarningText }}
            </pc-alert>
            <pc-checkbox
                v-model="allowsLarge"
                v-bind:label="allowLargeFilesLabel"
                switch
                class="mt-3"
                show-help
                v-bind:disabled="disableAllowLarge"
                v-on:help-clicked="toggleLargeAttachmentHelp" />
            <pc-transition>
                <div
                    v-if="showLargeAttachmentHelp"
                    class="alert alert-info mt-3 mb-0">
                    {{ largeAttachmentHelptext }}
                </div>
            </pc-transition>
        </div>
        <div>
            <pc-checkbox
                v-if="showMergeDocuments"
                v-model="canMerge"
                class="mt-3"
                v-bind:label="allowDocumentMergeLabel"
                switch
                show-help
                v-bind:disabled="disableCanMerge"
                v-on:help-clicked="toggleCanMergeAttachmentHelp" />
        </div>
        <pc-transition>
            <div
                v-if="showCanMergeAttachmentHelp"
                class="alert alert-info mt-3 mb-0">
                {{ canMergeHelptext }}
            </div>
        </pc-transition>
        <div v-if="programHasSensitiveData">
            <pc-checkbox
                v-model="isSensitive"
                v-bind:disabled="disableIsSensitive"
                v-bind:label="isSensitiveLabel"
                switch
                class="mt-3"
                show-help
                v-on:help-clicked="toggleSensitiveAttachmentHelp" />
            <pc-transition>
                <div
                    v-if="showSensitiveAttachmentHelp"
                    class="alert alert-info mt-3 mb-0">
                    {{ sensitiveAttachmentHelptext }}
                </div>
            </pc-transition>
            <pc-transition>
                <div
                    v-if="attachmentHasEnvelopeReferences"
                    class="alert alert-warning mt-3 mb-0">
                    {{ allowsLargeAttachmentsESignatureEnvelopesText }}
                </div>
            </pc-transition>
            <pc-transition>
                <div
                    v-if="attachmentHasCommunicationReferences"
                    class="alert alert-warning mt-3 mb-0">
                    {{ sensitiveAttachmentsCommunicationsTemplatesText }}
                </div>
            </pc-transition>
            <pc-transition>
                <div
                    v-if="isSensitive && !modelValue.IsSensitive"
                    class="alert alert-warning mt-3 mb-0">
                    {{ sensitiveAttachmentsIrreversibleText }}
                </div>
            </pc-transition>
        </div>
    </div>
</template>

<script>
    import { ALERT_SEVERITY } from "Util/alerts";
    import { deepClone } from "Util/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/editAttachmentCore.i18n";

    import { ATTACHMENT_REFERENCE_DOCUMENT_ALLOWED_EXTENSIONS } from "Projects/AttachmentView/attachmentConstants";

    export default {
        props: {
            modelValue: { type: Object, required: true },
            programHasConfigurableAttachments: { type: Boolean, required: true },
            programHasSensitiveData: { type: Boolean, required: true },
            largeAttachmentHelptext: { type: String, required: true },
            sensitiveAttachmentHelptext: { type: String, required: true },
            canMergeHelptext: { type: String, required: true },
            fileExtensionBlocklist: { type: Array, required: true },
            communicationAttachmentExtensionBlocklist: { type: Array, required: true },
            allowedMergeFileExtensions: { type: Array, required: true },
            showMergeDocuments: { type: Boolean, required: true },
            referencingCommunicationTemplates: { type: Array, required: false, default: () => [] },
            referencingSignatureEnvelopes: { type: Array, required: false, default: () => [] },
            disableAllFields: { type: Boolean, required: false, default: false }
        },
        emits: ["update:modelValue"],
        data() {
            return {
                showLargeAttachmentHelp: false,
                showCanMergeAttachmentHelp: false,
                showSensitiveAttachmentHelp: false,
                warning: ALERT_SEVERITY.warning,
                initialIsSensitive: this.modelValue.IsSensitive
            };
        },
        computed: {
            fileTypes: {
                get() {
                    return this.modelValue.FileTypes;
                },
                set(value) {
                    this.updateValue("FileTypes", value);
                }
            },
            allowsLarge: {
                get() {
                    return this.modelValue.AllowsLarge;
                },
                set(value) {
                    this.updateValue("AllowsLarge", value);
                }
            },
            canMerge: {
                get() {
                    return this.modelValue.CanMerge;
                },
                set(value) {
                    this.updateValue("CanMerge", value);
                }
            },
            isSensitive: {
                get() {
                    return this.modelValue.IsSensitive;
                },
                set(value) {
                    this.updateValue("IsSensitive", value);
                }
            },
            referenceDocuments() {
                // TODO_PROTO: ReferenceDocuments are not passed in where this is used in the form editor
                // That's not a huge deal, because the attachment extensions can't be edited in the form editor
                // once the attachment definition is in use. But it would be nice to have the reference documents
                return this.modelValue.ReferenceDocuments;
            },
            attachmentExtensions() {
                if (this.fileTypes) {
                    const matchedTypes = this.fileTypes.match(/[A-Za-z0-9]+/g);
                    return matchedTypes != null ? matchedTypes : [];
                } else return [];
            },
            attachmentBlocklistedTypes() {
                return this.attachmentExtensions.filter(
                    function (ext) {
                        return this.fileExtensionBlocklist.indexOf(ext) !== -1;
                    }.bind(this)
                );
            },
            attachmentDisallowedMergeFileTypeExtensions() {
                return this.attachmentExtensions.filter(
                    (ext) =>
                        !this.attachmentBlocklistedTypes.includes(ext) && !this.allowedMergeFileExtensions.includes(ext)
                );
            },
            attachmentCommunicationBlocklistedTypes() {
                return this.attachmentExtensions.filter(
                    (ext) =>
                        !this.attachmentBlocklistedTypes.includes(ext) &&
                        this.communicationAttachmentExtensionBlocklist.includes(ext)
                );
            },
            attachmentHasCommunicationReferences() {
                return (
                    this.referencingCommunicationTemplates.filter(function (template) {
                        return template.PublicId !== null;
                    }).length > 0
                );
            },
            attachmentHasEnvelopeReferences() {
                return this.referencingSignatureEnvelopes.length > 0;
            },
            attachmentCommunicationAlertClass() {
                return {
                    "alert-danger": this.attachmentHasCommunicationReferences,
                    "alert-warning": !this.attachmentHasCommunicationReferences
                };
            },
            disableFileExtensions() {
                return this.disableAllFields;
            },
            disableAllowLarge() {
                return this.disableAllFields || (this.attachmentHasEnvelopeReferences && !this.allowsLarge);
            },
            disableCanMerge() {
                return this.isSensitive || this.disableAllFields;
            },
            disableIsSensitive() {
                return (
                    (this.initialIsSensitive && !this.modelValue.IsNew) ||
                    this.attachmentHasCommunicationReferences ||
                    this.disableAllFields
                );
            },
            hasReferenceDocuments() {
                return this.referenceDocuments?.length > 0 ?? false;
            },
            ...mapI18n(
                {
                    ...KEYS,
                    blocklistedAttachmentTypesText() {
                        return {
                            key: KEYS.blocklistedAttachmentTypesText,
                            values: { fileTypes: this.attachmentBlocklistedTypes.join(", ") }
                        };
                    },
                    communicationUnsupportedAttachmentTypesText() {
                        return {
                            key: KEYS.communicationUnsupportedAttachmentTypesText,
                            values: { fileTypes: this.attachmentCommunicationBlocklistedTypes.join(", ") }
                        };
                    },
                    mergeTypeUnsupportedAttachmentTypesText() {
                        return {
                            key: KEYS.mergeTypeUnsupportedAttachmentTypesText,
                            values: { fileTypes: this.attachmentDisallowedMergeFileTypeExtensions.join(", ") }
                        };
                    },
                    referenceDocumentExtensionsWarningText() {
                        return {
                            key: KEYS.referenceDocumentExtensionsWarningText,
                            values: { fileTypes: ATTACHMENT_REFERENCE_DOCUMENT_ALLOWED_EXTENSIONS.join(", ") }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            isSensitive(newVal) {
                if (newVal) {
                    this.canMerge = false;
                }
            }
        },
        methods: {
            toggleLargeAttachmentHelp() {
                this.showLargeAttachmentHelp = !this.showLargeAttachmentHelp;
            },
            toggleSensitiveAttachmentHelp() {
                this.showSensitiveAttachmentHelp = !this.showSensitiveAttachmentHelp;
            },
            toggleCanMergeAttachmentHelp() {
                this.showCanMergeAttachmentHelp = !this.showCanMergeAttachmentHelp;
            },
            updateValue(property, data) {
                const value = deepClone(this.modelValue);
                value[property] = data;
                this.$emit("update:modelValue", value);
            }
        }
    };
</script>
